import { STANDARD_BRANCH } from '@festina-life/api'
import { errorMessageToastContent } from '@festina-life/components'
import { dateUtils, type Optional, urlUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'

import { useConfigurationContext } from '../../context/configurationContext'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useMutateConvertReconciliationSpecCreateOperationToExcel } from '../../uiModels/api/reconciliationSpecs/useMutateConvertReconciliationSpecCreateOperationToExcel'
import { useMutateFetchStandardReconciliationSpecExcel } from '../../uiModels/api/reconciliationSpecs/useMutateFetchStandardReconciliationSpecExcel'
import type { IndexedReconciliationSpecOperationWithMetaData } from '../../uiModels/reconciliationSpecOperations/types'

type Props = {
  /** Version in scope, if any exists at the current view date. */
  reconciliationSpecOperation: Optional<IndexedReconciliationSpecOperationWithMetaData>
}

const excelFileName = (specId: string, from: Optional<string>) =>
  `${[specId, from].filter(Boolean).join('-')}.xlsx`

/**
 * Exports the reconciliation-spec version currently in scope as the Excel document it
 * is authored in, which is the only way to get at its sheets from the portal.
 */
export const ReconciliationSpecDownloadVersionButton = ({
  reconciliationSpecOperation,
}: Props) => {
  const translate = useTranslate()
  const { branch } = useNavigationParams()
  const { viewAt } = useConfigurationContext()
  const { mutateAsync: convertToExcel, isPending: isConverting } =
    useMutateConvertReconciliationSpecCreateOperationToExcel()
  const { mutateAsync: fetchStandardExcel, isPending: isFetching } =
    useMutateFetchStandardReconciliationSpecExcel()

  const reconciliationSpec =
    reconciliationSpecOperation?.internalOperation.reconciliationSpec
  const specId = reconciliationSpec?.specId

  if (!reconciliationSpecOperation || !specId) {
    return null
  }

  const handleDownload = async () => {
    try {
      // The configuration component, and with it its conversion service, is not
      // available on the standard branch, where the connected component serves the
      // document itself.
      const excelDocument =
        branch === STANDARD_BRANCH
          ? await fetchStandardExcel({
              specId,
              at: dateUtils.dateAsServerStr(reconciliationSpec.from ?? viewAt),
            })
          : await convertToExcel({
              reconciliationSpecCreateOperation:
                reconciliationSpecOperation.internalOperation,
            })

      urlUtils.downloadAsFile(
        excelDocument,
        excelFileName(specId, reconciliationSpec.from),
      )
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    }
  }

  return (
    <Button
      startIcon="ArrowDownTray"
      isLoading={isConverting || isFetching}
      onClick={handleDownload}
    >
      {translate('reconciliationSpecViewer.topBar.actions.downloadVersion')}
    </Button>
  )
}
